import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopSubscriptionPlans.css';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { Skeleton } from '../../effects';
import iconBilling from '../../../assets/images/icons/icon-billing.svg';
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import { trackEvent } from '../../../utils/analytics';

const PLAN_CONFIG = [
  {
    id: 'free',
    name: 'Free',
    className: 'plan-free',
    badgeKey: 'mobilePlans.plans.free.badge',
    descriptionKey: 'mobilePlans.plans.free.description',
    featuresKey: 'mobilePlans.plans.free.features',
    prices: {
      monthly: { label: '0€', code: null }
    },
  },
  {
    id: 'pro',
    name: 'Pro',
    className: 'plan-pro',
    highlight: true,
    badgeKey: 'mobilePlans.plans.pro.badge',
    descriptionKey: 'mobilePlans.plans.pro.description',
    featuresKey: 'mobilePlans.plans.pro.features',
    prices: {
      monthly: { amount: 29, code: 'pro_monthly' },
      yearly: { amount: 290, code: 'pro_yearly', noteKey: 'mobilePlans.plans.pro.prices.yearly.note' }
    },
  },
  {
    id: 'ultra',
    name: 'Ultra',
    className: 'plan-ultra',
    badgeKey: 'mobilePlans.plans.ultra.badge',
    descriptionKey: 'mobilePlans.plans.ultra.description',
    featuresKey: 'mobilePlans.plans.ultra.features',
    prices: {
      monthly: { amount: 99, code: 'ultra_monthly' },
      yearly: { amount: 990, code: 'ultra_yearly', noteKey: 'mobilePlans.plans.ultra.prices.yearly.note' }
    },
  }
];

const CheckIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M16.6667 5.8335L8.33335 14.1668L3.33335 9.16683" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const Pill = ({ children, variant = 'default' }) => (
  <span className={`loopino-pill loopino-subscription-pill ${variant}`}>
    {children}
  </span>
);

const DesktopSubscriptionPlansContent = () => {
  const { t, language } = useLanguage();
  const { notify } = useDesktopAlert();
  const { get, post } = useApi();

  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [subscription, setSubscription] = useState(null);
  const [isStatusLoading, setIsStatusLoading] = useState(false);
  const [processingAction, setProcessingAction] = useState(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);

  const billingAlignedRef = useRef(false);

  const currencyFormatter = useMemo(() => {
    try {
      return new Intl.NumberFormat(language || 'it', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      });
    } catch (error) {
      console.warn('[DesktopSubscriptionPlans] Falling back to default currency formatter', error);
      return new Intl.NumberFormat('it', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 0,
        minimumFractionDigits: 0
      });
    }
  }, [language]);

  const dateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('[DesktopSubscriptionPlans] Falling back to default date formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }, [language]);

  const parseDate = useCallback((value) => {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? null : value;
    }

    if (typeof value === 'number') {
      const millis = value > 1e12 ? value : value * 1000;
      const date = new Date(millis);
      return Number.isNaN(date.getTime()) ? null : date;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;
      const date = new Date(trimmed);
      if (!Number.isNaN(date.getTime())) {
        return date;
      }
      const fallback = new Date(trimmed.replace(' ', 'T'));
      return Number.isNaN(fallback.getTime()) ? null : fallback;
    }

    return null;
  }, []);

  const formatDate = useCallback((value) => {
    const parsed = parseDate(value);
    if (!parsed) {
      return '--';
    }

    try {
      return dateFormatter.format(parsed);
    } catch (error) {
      console.warn('[DesktopSubscriptionPlans] Failed to format date', { value, error });
      return parsed.toLocaleDateString();
    }
  }, [dateFormatter, parseDate]);

  const activePlanCode = (subscription?.plan_code || subscription?.planCode || '').toLowerCase();

  const activePlanConfig = useMemo(() => {
    if (!activePlanCode) {
      return PLAN_CONFIG[0];
    }

    return PLAN_CONFIG.find((plan) => {
      return Object.values(plan.prices || {}).some(
        (price) => price?.code && price.code.toLowerCase() === activePlanCode
      );
    }) || PLAN_CONFIG[0];
  }, [activePlanCode]);

  const planConfig = useMemo(() => {
    return PLAN_CONFIG.map((plan) => {
      const features = t(plan.featuresKey, { returnObjects: true });
      const resolvedFeatures = Array.isArray(features) ? features : [];

      const yearly = plan.prices?.yearly;
      const resolvedYearly = yearly?.noteKey
        ? { ...yearly, note: t(yearly.noteKey) }
        : yearly;

      return {
        ...plan,
        badge: t(plan.badgeKey, { defaultValue: plan.name }),
        description: t(plan.descriptionKey),
        features: resolvedFeatures,
        prices: {
          ...plan.prices,
          ...(resolvedYearly ? { yearly: resolvedYearly } : {})
        }
      };
    });
  }, [t]);

  const alignBillingPeriodToPlan = useCallback(() => {
    if (billingAlignedRef.current || !activePlanCode) {
      return;
    }

    if (activePlanCode.includes('year')) {
      setBillingPeriod('yearly');
      billingAlignedRef.current = true;
    } else if (activePlanCode.includes('month')) {
      setBillingPeriod('monthly');
      billingAlignedRef.current = true;
    }
  }, [activePlanCode]);

  const loadSubscription = useCallback(async () => {
    setIsStatusLoading(true);
    try {
      const response = await get('/api/v1/user/ai-battery');
      const subscriptionData = response?.ai_battery?.subscription || response?.subscription || null;
      setSubscription(subscriptionData || null);
      billingAlignedRef.current = false;
    } catch (error) {
      if (error?.status === 401) {
        setSubscription(null);
        return;
      }

      const message = error?.message
        || t('mobilePlans.fetchError.desc', 'Impossibile leggere lo stato abbonamento');
      notify({
        variant: 'error',
        title: t('mobilePlans.fetchError.title', 'Errore nel recupero del piano'),
        description: message
      });
    } finally {
      setIsStatusLoading(false);
    }
  }, [get, notify, t]);

  useEffect(() => {
    loadSubscription();
  }, [loadSubscription]);

  useEffect(() => {
    alignBillingPeriodToPlan();
  }, [alignBillingPeriodToPlan]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const checkoutStatus = params.get('checkout');

    if (checkoutStatus === 'success') {
      notify({
        variant: 'success',
        title: t('mobilePlans.checkout.success.title', 'Pagamento completato'),
        description: t('mobilePlans.checkout.success.desc', 'Il piano e stato aggiornato, ricarico lo stato...')
      });
      trackEvent('subscription_checkout_success');
      loadSubscription();
    } else if (checkoutStatus === 'cancel') {
      notify({
        variant: 'warning',
        title: t('mobilePlans.checkout.cancel.title', 'Checkout annullato'),
        description: t('mobilePlans.checkout.cancel.desc', 'Nessun addebito. Puoi riprovare quando vuoi.')
      });
      trackEvent('subscription_checkout_cancel');
    } else {
      return;
    }

    params.delete('checkout');
    const newSearch = params.toString();
    const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}${window.location.hash || ''}`;
    window.history.replaceState({}, '', newUrl);
  }, [loadSubscription, notify, t]);

  const resolvePlanName = useCallback((code) => {
    if (!code) {
      return null;
    }
    const normalized = code.toLowerCase();
    for (const plan of PLAN_CONFIG) {
      const prices = Object.values(plan.prices || {});
      const match = prices.find((price) => price?.code && price.code.toLowerCase() === normalized);
      if (match) {
        return plan.name;
      }
    }
    return null;
  }, []);

  const openExternal = useCallback((url) => {
    if (typeof window === 'undefined' || !url) {
      return false;
    }

    const newTab = window.open(url, '_blank', 'noopener,noreferrer');
    if (newTab) {
      if (typeof newTab.focus === 'function') {
        newTab.focus();
      }
      return true;
    }

    notify({
      variant: 'warning',
      title: t('desktopPlans.externalPopup.title', 'Popup bloccato'),
      description: t('desktopPlans.externalPopup.desc', 'Apri pagina cliccando qui.'),
      actions: [
        {
          label: t('desktopPlans.externalPopup.action', 'Apri pagina'),
          variant: 'primary',
          onClick: () => window.open(url, '_blank', 'noopener,noreferrer')
        }
      ]
    });

    return false;
  }, [notify, t]);

  const buildReturnUrls = useCallback(() => {
    if (typeof window === 'undefined') {
      return {};
    }

    try {
      const successUrl = new URL(window.location.href);
      successUrl.searchParams.set('checkout', 'success');

      const cancelUrl = new URL(window.location.href);
      cancelUrl.searchParams.set('checkout', 'cancel');

      return {
        success: successUrl.toString(),
        cancel: cancelUrl.toString()
      };
    } catch (error) {
      console.warn('[DesktopSubscriptionPlans] Failed to build return URLs', error);
      return {};
    }
  }, []);

  const handleChangePlanNow = useCallback(async (planCode) => {
    if (!planCode) {
      return;
    }

    setProcessingAction({ type: 'now', planCode });
    trackEvent('subscription_change_now', { plan_code: planCode });
    try {
      const { success, cancel } = buildReturnUrls();
      const payload = { plan_code: planCode };
      if (success) {
        payload.success_url = success;
      }
      if (cancel) {
        payload.cancel_url = cancel;
      }

      const response = await post('/api/v1/payments/change-plan-now', payload);
      const paymentRequired = response?.payment_required ?? response?.paymentRequired ?? false;
      const redirectUrl = response?.url;

      if (paymentRequired) {
        if (redirectUrl) {
          notify({
            variant: 'info',
            title: t('mobilePlans.changeNow.redirect.title', 'Pagamento richiesto'),
            description: t('mobilePlans.changeNow.redirect.desc', 'Ti portiamo a Stripe per saldare la differenza.')
          });
          openExternal(redirectUrl);
          return;
        }

        notify({
          variant: 'error',
          title: t('mobilePlans.changeNow.redirectError.title', 'Checkout non disponibile'),
          description: t('mobilePlans.changeNow.redirectError.desc', 'Non ho ricevuto il link di pagamento. Riprova piu tardi.')
        });
        return;
      }

      notify({
        variant: 'success',
        title: t('mobilePlans.changeNow.success.title', 'Piano aggiornato subito'),
        description: t('mobilePlans.changeNow.success.desc', 'Il credito residuo e stato proratato. Rileggo lo stato...')
      });
      await loadSubscription();
    } catch (error) {
      const message = error?.errorData?.message || error?.message || t('mobilePlans.errors.unexpected', { defaultValue: 'Errore inatteso' });
      notify({
        variant: 'error',
        title: t('mobilePlans.changeNow.error.title', 'Cambio immediato non riuscito'),
        description: message
      });
    } finally {
      setProcessingAction(null);
    }
  }, [buildReturnUrls, loadSubscription, notify, openExternal, post, t]);

  const handleSchedulePlanChange = useCallback(async (planCode) => {
    if (!planCode) {
      return;
    }

    setProcessingAction({ type: 'schedule', planCode });
    trackEvent('subscription_change_schedule', { plan_code: planCode });
    try {
      await post('/api/v1/payments/schedule-plan-change', { plan_code: planCode });
      notify({
        variant: 'success',
        title: t('mobilePlans.schedule.success.title', 'Cambio programmato'),
        description: t('mobilePlans.schedule.success.desc', 'Il nuovo piano partira al prossimo rinnovo.')
      });
      await loadSubscription();
    } catch (error) {
      const message = error?.errorData?.message || error?.message || t('mobilePlans.errors.unexpected', { defaultValue: 'Errore inatteso' });
      notify({
        variant: 'error',
        title: t('mobilePlans.schedule.error.title', 'Programmazione non riuscita'),
        description: message
      });
    } finally {
      setProcessingAction(null);
    }
  }, [loadSubscription, notify, post, t]);

  const handleOpenBillingPortal = useCallback(async () => {
    setIsPortalLoading(true);
    trackEvent('subscription_portal_open');
    try {
      const returnUrl = typeof window !== 'undefined' ? window.location.href : undefined;
      const payload = returnUrl ? { return_url: returnUrl } : {};
      const response = await post('/api/v1/payments/billing-portal', payload);
      const redirectUrl = response?.url;

      if (redirectUrl) {
        openExternal(redirectUrl);
        return;
      }

      notify({
        variant: 'error',
        title: t('mobilePlans.portal.missingUrl.title', 'Billing portal non disponibile'),
        description: t('mobilePlans.portal.missingUrl.desc', 'Nessun link ricevuto. Controlla piu tardi.')
      });
    } catch (error) {
      const message = error?.errorData?.message || error?.message || t('mobilePlans.errors.unexpected', { defaultValue: 'Errore inatteso' });
      notify({
        variant: 'error',
        title: t('mobilePlans.portal.error.title', 'Errore di billing'),
        description: message
      });
    } finally {
      setIsPortalLoading(false);
    }
  }, [notify, openExternal, post, t]);

  const statusKey = (subscription?.status || 'inactive').toLowerCase();
  const statusLabel = t(`mobilePlans.status.${statusKey}`, { defaultValue: t('mobilePlans.status.inactive', { defaultValue: 'Nessun abbonamento' }) });
  const statusVariant = (statusKey === 'active' || statusKey === 'trialing' || statusKey === 'grace')
    ? 'positive'
    : (statusKey === 'past_due' || statusKey === 'unpaid' ? 'warning' : 'muted');

  const nextRefresh = subscription?.next_refresh_at
    || subscription?.current_period_end
    || subscription?.period_end
    || null;

  const pendingPlanCode = (subscription?.pending_plan_code || subscription?.pendingPlanCode || '').toLowerCase();
  const currentPlanLabel = useMemo(() => {
    if (!subscription) {
      return t('mobilePlans.plans.free.name', { defaultValue: 'Free' });
    }
    const code = (subscription.plan_code || subscription.planCode || '').toLowerCase();
    const name = subscription.plan_name || subscription.planName;
    if (name) {
      return name;
    }
    if (code) {
      return resolvePlanName(code) || code;
    }
    return t('mobilePlans.plans.free.name', { defaultValue: 'Free' });
  }, [resolvePlanName, subscription, t]);

  const renderPlanCard = (plan) => {
    const selectedPrice = plan.prices[billingPeriod] || plan.prices.monthly;
    const targetCode = selectedPrice?.code || null;
    const isSamePlan = targetCode && targetCode.toLowerCase() === activePlanCode;
    const isProcessing = processingAction?.planCode
      ? processingAction.planCode === targetCode
      : false;
    const hasPendingChange = pendingPlanCode && targetCode
      ? pendingPlanCode === targetCode.toLowerCase()
      : false;
    const isActionDisabled = isProcessing || !targetCode || isSamePlan;
    const isScheduleDisabled = isActionDisabled || hasPendingChange;

    const periodLabel = billingPeriod === 'yearly'
      ? t('mobilePlans.period.year', 'anno')
      : t('mobilePlans.period.month', 'mese');

    const priceLabel = selectedPrice?.amount
      ? currencyFormatter.format(selectedPrice.amount)
      : (selectedPrice?.label || '--');

    const normalizedPlanName = plan.name ? plan.name.toLowerCase() : '';
    const actionNowLabel = t('desktopPlans.actions.switchNow', {
      defaultValue: normalizedPlanName ? `Passa a ${normalizedPlanName} adesso` : 'Passa al piano adesso',
      plan: normalizedPlanName
    });
    const actionScheduleLabel = t('desktopPlans.actions.switchOnRenewal', {
      defaultValue: normalizedPlanName
        ? `Passa a ${normalizedPlanName} dal prossimo rinnovo`
        : 'Passa al piano dal prossimo rinnovo',
      plan: normalizedPlanName
    });

    return (
      <div
        key={plan.id}
        className={[
          'loopino-plan-card',
          plan.className || '',
          plan.highlight ? 'is-highlighted' : '',
          isSamePlan ? 'is-current' : ''
        ].filter(Boolean).join(' ')}
      >
        <div className="loopino-plan-card-header">
          <div className="loopino-plan-chip">
            <img src={iconAi} alt="" aria-hidden="true" />
            <span>{plan.name}</span>
          </div>
          {plan.badge && <span className="loopino-plan-badge">{plan.badge}</span>}
        </div>

        <p className="loopino-plan-description">{plan.description}</p>

        <div className="loopino-plan-price-row">
          <div>
            <div className="loopino-plan-price">
              {priceLabel}
              <span className="loopino-plan-period">/{periodLabel}</span>
            </div>
            {selectedPrice?.note && (
              <div className="loopino-plan-note">{selectedPrice.note}</div>
            )}
          </div>
          <div className="loopino-plan-pills">
            {isSamePlan && (
              <Pill variant="status-positive">
                {t('mobilePlans.currentPlan', 'Piano attivo')}
              </Pill>
            )}
            {hasPendingChange && (
              <Pill variant="ghost">
                {t('mobilePlans.pendingPlan.label', 'Cambio programmato')}
              </Pill>
            )}
          </div>
        </div>

        <ul className="loopino-plan-features">
          {plan.features.map((feature) => (
            <li key={feature}>
              <CheckIcon size={14} />
              <span>{feature}</span>
            </li>
          ))}
        </ul>

        <div className="loopino-plan-actions">
          <button
            type="button"
            className="loopino-button"
            data-variant={plan.highlight ? 'primary' : 'surface'}
            onClick={() => handleChangePlanNow(targetCode)}
            disabled={isActionDisabled}
          >
            {isProcessing && processingAction?.type === 'now'
              ? t('mobilePlans.actions.processing', 'Attendi...')
              : actionNowLabel}
          </button>
          <button
            type="button"
            className="loopino-button loopino-plan-ghost"
            data-variant="ghost"
            onClick={() => handleSchedulePlanChange(targetCode)}
            disabled={isScheduleDisabled}
          >
            {isProcessing && processingAction?.type === 'schedule'
              ? t('mobilePlans.actions.processing', 'Attendi...')
              : actionScheduleLabel}
          </button>
          <button
            type="button"
            className="loopino-plan-link"
            onClick={() => handlePlanCheckout(targetCode)}
            disabled={isProcessing || isPortalLoading || !targetCode || isSamePlan}
          >
            {t('mobilePlans.actions.checkout', 'Checkout immediato')}
          </button>
        </div>
      </div>
    );
  };

  const renderSkeleton = () => (
    <div className="loopino-subscription-skeleton">
      <div className="loopino-subscription-skeleton-header">
        <Skeleton width="50%" height={32} shimmer />
        <Skeleton width="30%" height={18} shimmer />
      </div>
      <div className="loopino-subscription-skeleton-status">
        {[1, 2].map((item) => (
          <Skeleton key={`status-skeleton-${item}`} variant="rounded" width="100%" height={120} shimmer />
        ))}
      </div>
      <div className="loopino-subscription-skeleton-plans">
        {[1, 2, 3].map((item) => (
          <Skeleton key={`plan-skeleton-${item}`} variant="rounded" width="100%" height={280} shimmer />
        ))}
      </div>
    </div>
  );

  return (
    <div className="loopino-desktop-shell loopino-subscription-shell">
      <div className="loopino-desktop-grid">
        <header className="loopino-desktop-post-review-header">
          <div>
            <p className="loopino-desktop-post-review-eyebrow">
              {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
            </p>
            <h1>{t('mobilePlans.title', 'Scegli il tuo piano')}</h1>
            <p className="loopino-desktop-post-review-summary">
              {t('mobilePlans.subtitle', 'Potenzia la batteria AI e gestisci i pagamenti con Stripe.')}
            </p>
          </div>
          <div className="loopino-desktop-post-review-actions">
            <button
              type="button"
              className="loopino-button"
              data-variant="primary"
              onClick={handleOpenBillingPortal}
              disabled={isPortalLoading}
            >
              <img src={iconBilling} alt="" />
              <span>
                {isPortalLoading
                  ? t('mobilePlans.portal.loading', 'Apertura Billing Portal...')
                  : t('desktopPlans.manage', { defaultValue: 'Gestisci abbonamento' })}
              </span>
            </button>
          </div>
        </header>

        {isStatusLoading ? (
          renderSkeleton()
        ) : (
          <>
            <div className="loopino-subscription-status-grid">
              <div className="loopino-glass-panel loopino-subscription-card">
                <div className="loopino-subscription-card-header">
                  <div>
                    <p className="loopino-subscription-label">
                      {t('mobilePlans.current', 'Piano')}
                    </p>
                    <h3 className="loopino-subscription-card-title">{currentPlanLabel}</h3>
                  </div>
                  <Pill variant={`status-${statusVariant}`}>{statusLabel}</Pill>
                </div>
                {pendingPlanCode && (
                  <p className="loopino-subscription-note">
                    {t('mobilePlans.pendingPlan.hero', 'Cambio programmato al rinnovo')}: {resolvePlanName(pendingPlanCode) || pendingPlanCode}
                  </p>
                )}
              </div>

              <div className="loopino-glass-panel loopino-subscription-card">
                <div className="loopino-subscription-card-header">
                  <div>
                    <p className="loopino-subscription-label">
                      {t('mobilePlans.nextRefresh', 'Prossimo rinnovo')}
                    </p>
                    <h3 className="loopino-subscription-card-title">{formatDate(nextRefresh)}</h3>
                  </div>
                </div>
              </div>
            </div>

            <div className="loopino-subscription-toggle">
              <div className="loopino-subscription-toggle-control">
                <button
                  type="button"
                  className={`loopino-subscription-toggle-option ${billingPeriod === 'monthly' ? 'is-active' : ''}`}
                  onClick={() => setBillingPeriod('monthly')}
                >
                  {t('mobilePlans.period.monthly', 'Mensile')}
                </button>
                <button
                  type="button"
                  className={`loopino-subscription-toggle-option ${billingPeriod === 'yearly' ? 'is-active' : ''}`}
                  onClick={() => setBillingPeriod('yearly')}
                >
                  {t('mobilePlans.period.yearly', 'Annuale')}
                  <span className="loopino-subscription-toggle-save">
                    {t('mobilePlans.period.save', 'Risparmia')}
                  </span>
                </button>
              </div>
            </div>

            <div className="loopino-subscription-plans-grid">
              {planConfig.map((plan) => renderPlanCard(plan))}
            </div>
          </>
        )}

        <div className="loopino-subscription-footnote">
          <p>
            {t('mobilePlans.footnote', 'Il checkout avviene su Stripe in modo sicuro. Dopo il pagamento tornerai qui con lo stato aggiornato.')}
          </p>
        </div>
      </div>
    </div>
  );
};

const DesktopSubscriptionPlansPage = () => (
  <DesktopAlertProvider>
    <DesktopSubscriptionPlansContent />
  </DesktopAlertProvider>
);

export default DesktopSubscriptionPlansPage;
